"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MapLibreGL from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Loader2, Locate, Maximize, Minus, Plus, X } from "lucide-react";
import { createContext, forwardRef, useCallback, useContext, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils/cn";
const defaultStyles = {
    dark: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
    light: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
};
// Check document class for theme (works with next-themes, etc.)
function getDocumentTheme() {
    if (typeof document === "undefined")
        return null;
    if (document.documentElement.classList.contains("dark"))
        return "dark";
    if (document.documentElement.classList.contains("light"))
        return "light";
    return null;
}
// Get system preference
function getSystemTheme() {
    if (typeof window === "undefined")
        return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
}
function useResolvedTheme(themeProp) {
    const [detectedTheme, setDetectedTheme] = useState(() => getDocumentTheme() ?? getSystemTheme());
    useEffect(() => {
        if (themeProp)
            return; // Skip detection if theme is provided via prop
        // Watch for document class changes (e.g., next-themes toggling dark class)
        const observer = new MutationObserver(() => {
            const docTheme = getDocumentTheme();
            if (docTheme) {
                setDetectedTheme(docTheme);
            }
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });
        // Also watch for system preference changes
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleSystemChange = (e) => {
            // Only use system preference if no document class is set
            if (!getDocumentTheme()) {
                setDetectedTheme(e.matches ? "dark" : "light");
            }
        };
        mediaQuery.addEventListener("change", handleSystemChange);
        return () => {
            observer.disconnect();
            mediaQuery.removeEventListener("change", handleSystemChange);
        };
    }, [themeProp]);
    return themeProp ?? detectedTheme;
}
const MapContext = createContext(null);
function useMap() {
    const context = useContext(MapContext);
    if (!context) {
        throw new Error("useMap must be used within a Map component");
    }
    return context;
}
function DefaultLoader() {
    return (_jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: _jsxs("div", { className: "flex gap-1", children: [_jsx("span", { className: "size-1.5 animate-pulse rounded-full bg-muted-foreground/60" }), _jsx("span", { className: "size-1.5 animate-pulse rounded-full bg-muted-foreground/60 [animation-delay:150ms]" }), _jsx("span", { className: "size-1.5 animate-pulse rounded-full bg-muted-foreground/60 [animation-delay:300ms]" })] }) }));
}
function getViewport(map) {
    const center = map.getCenter();
    return {
        center: [center.lng, center.lat],
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
    };
}
const Map = forwardRef(function Map({ children, className, theme: themeProp, styles, projection, viewport, onViewportChange, ...props }, ref) {
    const containerRef = useRef(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isStyleLoaded, setIsStyleLoaded] = useState(false);
    const currentStyleRef = useRef(null);
    const styleTimeoutRef = useRef(null);
    const internalUpdateRef = useRef(false);
    const resolvedTheme = useResolvedTheme(themeProp);
    const isControlled = viewport !== undefined && onViewportChange !== undefined;
    const onViewportChangeRef = useRef(onViewportChange);
    onViewportChangeRef.current = onViewportChange;
    const mapStyles = useMemo(() => ({
        dark: styles?.dark ?? defaultStyles.dark,
        light: styles?.light ?? defaultStyles.light,
    }), [styles]);
    // Expose the map instance to the parent component
    useImperativeHandle(ref, () => mapInstance, [mapInstance]);
    const clearStyleTimeout = useCallback(() => {
        if (styleTimeoutRef.current) {
            clearTimeout(styleTimeoutRef.current);
            styleTimeoutRef.current = null;
        }
    }, []);
    // Initialize the map
    useEffect(() => {
        if (!containerRef.current)
            return;
        const initialStyle = resolvedTheme === "dark" ? mapStyles.dark : mapStyles.light;
        currentStyleRef.current = initialStyle;
        const map = new MapLibreGL.Map({
            container: containerRef.current,
            style: initialStyle,
            renderWorldCopies: false,
            attributionControl: {
                compact: true,
            },
            ...props,
            ...viewport,
        });
        const styleDataHandler = () => {
            clearStyleTimeout();
            // Delay to ensure style is fully processed before allowing layer operations
            // This is a workaround to avoid race conditions with the style loading
            // else we have to force update every layer on setStyle change
            styleTimeoutRef.current = setTimeout(() => {
                setIsStyleLoaded(true);
                if (projection) {
                    map.setProjection(projection);
                }
            }, 100);
        };
        const loadHandler = () => setIsLoaded(true);
        // Viewport change handler - skip if triggered by internal update
        const handleMove = () => {
            if (internalUpdateRef.current)
                return;
            onViewportChangeRef.current?.(getViewport(map));
        };
        map.on("load", loadHandler);
        map.on("styledata", styleDataHandler);
        map.on("move", handleMove);
        setMapInstance(map);
        return () => {
            clearStyleTimeout();
            map.off("load", loadHandler);
            map.off("styledata", styleDataHandler);
            map.off("move", handleMove);
            map.remove();
            setIsLoaded(false);
            setIsStyleLoaded(false);
            setMapInstance(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Sync controlled viewport to map
    useEffect(() => {
        if (!mapInstance || !isControlled || !viewport)
            return;
        if (mapInstance.isMoving())
            return;
        const current = getViewport(mapInstance);
        const next = {
            center: viewport.center ?? current.center,
            zoom: viewport.zoom ?? current.zoom,
            bearing: viewport.bearing ?? current.bearing,
            pitch: viewport.pitch ?? current.pitch,
        };
        if (next.center[0] === current.center[0] &&
            next.center[1] === current.center[1] &&
            next.zoom === current.zoom &&
            next.bearing === current.bearing &&
            next.pitch === current.pitch) {
            return;
        }
        internalUpdateRef.current = true;
        mapInstance.jumpTo(next);
        internalUpdateRef.current = false;
    }, [mapInstance, isControlled, viewport]);
    // Handle style change
    useEffect(() => {
        if (!mapInstance || !resolvedTheme)
            return;
        const newStyle = resolvedTheme === "dark" ? mapStyles.dark : mapStyles.light;
        if (currentStyleRef.current === newStyle)
            return;
        clearStyleTimeout();
        currentStyleRef.current = newStyle;
        setIsStyleLoaded(false);
        mapInstance.setStyle(newStyle, { diff: true });
    }, [mapInstance, resolvedTheme, mapStyles, clearStyleTimeout]);
    const contextValue = useMemo(() => ({
        map: mapInstance,
        isLoaded: isLoaded && isStyleLoaded,
    }), [mapInstance, isLoaded, isStyleLoaded]);
    return (_jsx(MapContext.Provider, { value: contextValue, children: _jsxs("div", { ref: containerRef, className: cn("relative h-full w-full", className), children: [!isLoaded && _jsx(DefaultLoader, {}), mapInstance && children] }) }));
});
const MarkerContext = createContext(null);
function useMarkerContext() {
    const context = useContext(MarkerContext);
    if (!context) {
        throw new Error("Marker components must be used within MapMarker");
    }
    return context;
}
function MapMarker({ id, longitude, latitude, children, onClick, onMouseEnter, onMouseLeave, onDragStart, onDrag, onDragEnd, draggable = false, ...markerOptions }) {
    const { map } = useMap();
    const callbacksRef = useRef({
        onClick,
        onMouseEnter,
        onMouseLeave,
        onDragStart,
        onDrag,
        onDragEnd,
    });
    callbacksRef.current = {
        onClick,
        onMouseEnter,
        onMouseLeave,
        onDragStart,
        onDrag,
        onDragEnd,
    };
    const marker = useMemo(() => {
        const element = document.createElement("div");
        if (id)
            element.id = id;
        const markerInstance = new MapLibreGL.Marker({
            ...markerOptions,
            element,
            draggable,
        }).setLngLat([longitude, latitude]);
        const handleClick = (e) => callbacksRef.current.onClick?.(e);
        const handleMouseEnter = (e) => callbacksRef.current.onMouseEnter?.(e);
        const handleMouseLeave = (e) => callbacksRef.current.onMouseLeave?.(e);
        markerInstance.getElement()?.addEventListener("click", handleClick);
        markerInstance
            .getElement()
            ?.addEventListener("mouseenter", handleMouseEnter);
        markerInstance
            .getElement()
            ?.addEventListener("mouseleave", handleMouseLeave);
        const handleDragStart = () => {
            const lngLat = markerInstance.getLngLat();
            callbacksRef.current.onDragStart?.({ lng: lngLat.lng, lat: lngLat.lat });
        };
        const handleDrag = () => {
            const lngLat = markerInstance.getLngLat();
            callbacksRef.current.onDrag?.({ lng: lngLat.lng, lat: lngLat.lat });
        };
        const handleDragEnd = () => {
            const lngLat = markerInstance.getLngLat();
            callbacksRef.current.onDragEnd?.({ lng: lngLat.lng, lat: lngLat.lat });
        };
        markerInstance.on("dragstart", handleDragStart);
        markerInstance.on("drag", handleDrag);
        markerInstance.on("dragend", handleDragEnd);
        return markerInstance;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!map)
            return;
        marker.addTo(map);
        return () => {
            marker.remove();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);
    if (marker.getLngLat().lng !== longitude ||
        marker.getLngLat().lat !== latitude) {
        marker.setLngLat([longitude, latitude]);
    }
    if (marker.isDraggable() !== draggable) {
        marker.setDraggable(draggable);
    }
    const currentOffset = marker.getOffset();
    const newOffset = markerOptions.offset ?? [0, 0];
    const [newOffsetX, newOffsetY] = Array.isArray(newOffset)
        ? newOffset
        : [newOffset.x, newOffset.y];
    if (currentOffset.x !== newOffsetX || currentOffset.y !== newOffsetY) {
        marker.setOffset(newOffset);
    }
    if (marker.getRotation() !== markerOptions.rotation) {
        marker.setRotation(markerOptions.rotation ?? 0);
    }
    if (marker.getRotationAlignment() !== markerOptions.rotationAlignment) {
        marker.setRotationAlignment(markerOptions.rotationAlignment ?? "auto");
    }
    if (marker.getPitchAlignment() !== markerOptions.pitchAlignment) {
        marker.setPitchAlignment(markerOptions.pitchAlignment ?? "auto");
    }
    return (_jsx(MarkerContext.Provider, { value: { marker, map }, children: children }));
}
function MarkerContent({ children, className }) {
    const { marker } = useMarkerContext();
    return createPortal(_jsx("div", { className: cn("relative cursor-pointer", className), children: children || _jsx(DefaultMarkerIcon, {}) }), marker.getElement());
}
function DefaultMarkerIcon() {
    return (_jsx("div", { className: "relative h-4 w-4 rounded-full border-2 border-white bg-blue-500 shadow-lg" }));
}
function MarkerPopup({ children, className, closeButton = false, ...popupOptions }) {
    const { marker, map } = useMarkerContext();
    const container = useMemo(() => document.createElement("div"), []);
    const prevPopupOptions = useRef(popupOptions);
    const popup = useMemo(() => {
        const popupInstance = new MapLibreGL.Popup({
            offset: 16,
            ...popupOptions,
            closeButton: false,
        })
            .setMaxWidth("none")
            .setDOMContent(container);
        return popupInstance;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!map)
            return;
        popup.setDOMContent(container);
        marker.setPopup(popup);
        return () => {
            marker.setPopup(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);
    if (popup.isOpen()) {
        const prev = prevPopupOptions.current;
        if (prev.offset !== popupOptions.offset) {
            popup.setOffset(popupOptions.offset ?? 16);
        }
        if (prev.maxWidth !== popupOptions.maxWidth && popupOptions.maxWidth) {
            popup.setMaxWidth(popupOptions.maxWidth ?? "none");
        }
        prevPopupOptions.current = popupOptions;
    }
    const handleClose = () => popup.remove();
    return createPortal(_jsxs("div", { className: cn("relative rounded-md border bg-popover p-3 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95", className), children: [closeButton && (_jsxs("button", { type: "button", onClick: handleClose, className: "absolute right-1 top-1 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2", "aria-label": "Close popup", children: [_jsx(X, { className: "h-4 w-4" }), _jsx("span", { className: "sr-only", children: "Close" })] })), children] }), container);
}
function MarkerTooltip({ children, className, ...popupOptions }) {
    const { marker, map } = useMarkerContext();
    const container = useMemo(() => document.createElement("div"), []);
    const prevTooltipOptions = useRef(popupOptions);
    const tooltip = useMemo(() => {
        const tooltipInstance = new MapLibreGL.Popup({
            offset: 16,
            ...popupOptions,
            closeOnClick: true,
            closeButton: false,
        }).setMaxWidth("none");
        return tooltipInstance;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!map)
            return;
        tooltip.setDOMContent(container);
        const handleMouseEnter = () => {
            tooltip.setLngLat(marker.getLngLat()).addTo(map);
        };
        const handleMouseLeave = () => tooltip.remove();
        marker.getElement()?.addEventListener("mouseenter", handleMouseEnter);
        marker.getElement()?.addEventListener("mouseleave", handleMouseLeave);
        return () => {
            marker.getElement()?.removeEventListener("mouseenter", handleMouseEnter);
            marker.getElement()?.removeEventListener("mouseleave", handleMouseLeave);
            tooltip.remove();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);
    if (tooltip.isOpen()) {
        const prev = prevTooltipOptions.current;
        if (prev.offset !== popupOptions.offset) {
            tooltip.setOffset(popupOptions.offset ?? 16);
        }
        if (prev.maxWidth !== popupOptions.maxWidth && popupOptions.maxWidth) {
            tooltip.setMaxWidth(popupOptions.maxWidth ?? "none");
        }
        prevTooltipOptions.current = popupOptions;
    }
    return createPortal(_jsx("div", { className: cn("rounded-md bg-foreground px-2 py-1 text-xs text-background shadow-md animate-in fade-in-0 zoom-in-95", className), children: children }), container);
}
function MarkerLabel({ children, className, position = "top", }) {
    const positionClasses = {
        top: "bottom-full mb-1",
        bottom: "top-full mt-1",
    };
    return (_jsx("div", { className: cn("absolute left-1/2 -translate-x-1/2 whitespace-nowrap", "text-[10px] font-medium text-foreground", positionClasses[position], className), children: children }));
}
const positionClasses = {
    "top-left": "top-2 left-2",
    "top-right": "top-2 right-2",
    "bottom-left": "bottom-2 left-2",
    "bottom-right": "bottom-10 right-2",
};
function ControlGroup({ children }) {
    return (_jsx("div", { className: "flex flex-col overflow-hidden rounded-md border border-border bg-background shadow-sm [&>button:not(:last-child)]:border-b [&>button:not(:last-child)]:border-border", children: children }));
}
function ControlButton({ onClick, label, children, disabled = false, }) {
    return (_jsx("button", { onClick: onClick, "aria-label": label, type: "button", className: cn("flex size-8 items-center justify-center transition-colors hover:bg-accent dark:hover:bg-accent/40", disabled && "pointer-events-none cursor-not-allowed opacity-50"), disabled: disabled, children: children }));
}
function MapControls({ position = "bottom-right", showZoom = true, showCompass = false, showLocate = false, showFullscreen = false, className, onLocate, }) {
    const { map } = useMap();
    const [waitingForLocation, setWaitingForLocation] = useState(false);
    const handleZoomIn = useCallback(() => {
        map?.zoomTo(map.getZoom() + 1, { duration: 300 });
    }, [map]);
    const handleZoomOut = useCallback(() => {
        map?.zoomTo(map.getZoom() - 1, { duration: 300 });
    }, [map]);
    const handleResetBearing = useCallback(() => {
        map?.resetNorthPitch({ duration: 300 });
    }, [map]);
    const handleLocate = useCallback(() => {
        setWaitingForLocation(true);
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition((pos) => {
                const coords = {
                    longitude: pos.coords.longitude,
                    latitude: pos.coords.latitude,
                };
                map?.flyTo({
                    center: [coords.longitude, coords.latitude],
                    zoom: 14,
                    duration: 1500,
                });
                onLocate?.(coords);
                setWaitingForLocation(false);
            }, (error) => {
                setWaitingForLocation(false);
            });
        }
    }, [map, onLocate]);
    const handleFullscreen = useCallback(() => {
        const container = map?.getContainer();
        if (!container)
            return;
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
        else {
            container.requestFullscreen();
        }
    }, [map]);
    return (_jsxs("div", { className: cn("absolute z-10 flex flex-col gap-1.5", positionClasses[position], className), children: [showZoom && (_jsxs(ControlGroup, { children: [_jsx(ControlButton, { onClick: handleZoomIn, label: "Zoom in", children: _jsx(Plus, { className: "size-4" }) }), _jsx(ControlButton, { onClick: handleZoomOut, label: "Zoom out", children: _jsx(Minus, { className: "size-4" }) })] })), showCompass && (_jsx(ControlGroup, { children: _jsx(CompassButton, { onClick: handleResetBearing }) })), showLocate && (_jsx(ControlGroup, { children: _jsx(ControlButton, { onClick: handleLocate, label: "Find my location", disabled: waitingForLocation, children: waitingForLocation ? (_jsx(Loader2, { className: "size-4 animate-spin" })) : (_jsx(Locate, { className: "size-4" })) }) })), showFullscreen && (_jsx(ControlGroup, { children: _jsx(ControlButton, { onClick: handleFullscreen, label: "Toggle fullscreen", children: _jsx(Maximize, { className: "size-4" }) }) }))] }));
}
function CompassButton({ onClick }) {
    const { map } = useMap();
    const compassRef = useRef(null);
    useEffect(() => {
        if (!map || !compassRef.current)
            return;
        const compass = compassRef.current;
        const updateRotation = () => {
            const bearing = map.getBearing();
            const pitch = map.getPitch();
            compass.style.transform = `rotateX(${pitch}deg) rotateZ(${-bearing}deg)`;
        };
        map.on("rotate", updateRotation);
        map.on("pitch", updateRotation);
        updateRotation();
        return () => {
            map.off("rotate", updateRotation);
            map.off("pitch", updateRotation);
        };
    }, [map]);
    return (_jsx(ControlButton, { onClick: onClick, label: "Reset bearing to north", children: _jsxs("svg", { ref: compassRef, viewBox: "0 0 24 24", className: "size-5 transition-transform duration-200", style: { transformStyle: "preserve-3d" }, children: [_jsx("path", { d: "M12 2L16 12H12V2Z", className: "fill-red-500" }), _jsx("path", { d: "M12 2L8 12H12V2Z", className: "fill-red-300" }), _jsx("path", { d: "M12 22L16 12H12V22Z", className: "fill-muted-foreground/60" }), _jsx("path", { d: "M12 22L8 12H12V22Z", className: "fill-muted-foreground/30" })] }) }));
}
function MapPopup({ longitude, latitude, onClose, children, className, closeButton = false, ...popupOptions }) {
    const { map } = useMap();
    const popupOptionsRef = useRef(popupOptions);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const container = useMemo(() => document.createElement("div"), []);
    const popup = useMemo(() => {
        const popupInstance = new MapLibreGL.Popup({
            offset: 16,
            ...popupOptions,
            closeButton: false,
        })
            .setMaxWidth("none")
            .setLngLat([longitude, latitude]);
        return popupInstance;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!map)
            return;
        const onCloseProp = () => onCloseRef.current?.();
        popup.on("close", onCloseProp);
        popup.setDOMContent(container);
        popup.addTo(map);
        return () => {
            popup.off("close", onCloseProp);
            if (popup.isOpen()) {
                popup.remove();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);
    if (popup.isOpen()) {
        const prev = popupOptionsRef.current;
        if (popup.getLngLat().lng !== longitude ||
            popup.getLngLat().lat !== latitude) {
            popup.setLngLat([longitude, latitude]);
        }
        if (prev.offset !== popupOptions.offset) {
            popup.setOffset(popupOptions.offset ?? 16);
        }
        if (prev.maxWidth !== popupOptions.maxWidth && popupOptions.maxWidth) {
            popup.setMaxWidth(popupOptions.maxWidth ?? "none");
        }
        popupOptionsRef.current = popupOptions;
    }
    const handleClose = () => {
        popup.remove();
    };
    return createPortal(_jsxs("div", { className: cn("relative rounded-md border bg-popover p-3 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95", className), children: [closeButton && (_jsxs("button", { type: "button", onClick: handleClose, className: "absolute right-1 top-1 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2", "aria-label": "Close popup", children: [_jsx(X, { className: "h-4 w-4" }), _jsx("span", { className: "sr-only", children: "Close" })] })), children] }), container);
}
function MapRoute({ id: propId, coordinates, color = "#4285F4", width = 3, opacity = 0.8, dashArray, onClick, onMouseEnter, onMouseLeave, interactive = true, }) {
    const { map, isLoaded } = useMap();
    const autoId = useId();
    const id = propId ?? autoId;
    const sourceId = `route-source-${id}`;
    const layerId = `route-layer-${id}`;
    // Add source and layer on mount
    useEffect(() => {
        if (!isLoaded || !map)
            return;
        map.addSource(sourceId, {
            type: "geojson",
            data: {
                type: "Feature",
                properties: {},
                geometry: { type: "LineString", coordinates: [] },
            },
        });
        map.addLayer({
            id: layerId,
            type: "line",
            source: sourceId,
            layout: { "line-join": "round", "line-cap": "round" },
            paint: {
                "line-color": color,
                "line-width": width,
                "line-opacity": opacity,
                ...(dashArray && { "line-dasharray": dashArray }),
            },
        });
        return () => {
            try {
                if (map.getLayer(layerId))
                    map.removeLayer(layerId);
                if (map.getSource(sourceId))
                    map.removeSource(sourceId);
            }
            catch {
                // ignore
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, map]);
    // When coordinates change, update the source data
    useEffect(() => {
        if (!isLoaded || !map || coordinates.length < 2)
            return;
        const source = map.getSource(sourceId);
        if (source) {
            source.setData({
                type: "Feature",
                properties: {},
                geometry: { type: "LineString", coordinates },
            });
        }
    }, [isLoaded, map, coordinates, sourceId]);
    useEffect(() => {
        if (!isLoaded || !map || !map.getLayer(layerId))
            return;
        map.setPaintProperty(layerId, "line-color", color);
        map.setPaintProperty(layerId, "line-width", width);
        map.setPaintProperty(layerId, "line-opacity", opacity);
        if (dashArray) {
            map.setPaintProperty(layerId, "line-dasharray", dashArray);
        }
    }, [isLoaded, map, layerId, color, width, opacity, dashArray]);
    // Handle click and hover events
    useEffect(() => {
        if (!isLoaded || !map || !interactive)
            return;
        const handleClick = () => {
            onClick?.();
        };
        const handleMouseEnter = () => {
            map.getCanvas().style.cursor = "pointer";
            onMouseEnter?.();
        };
        const handleMouseLeave = () => {
            map.getCanvas().style.cursor = "";
            onMouseLeave?.();
        };
        map.on("click", layerId, handleClick);
        map.on("mouseenter", layerId, handleMouseEnter);
        map.on("mouseleave", layerId, handleMouseLeave);
        return () => {
            map.off("click", layerId, handleClick);
            map.off("mouseenter", layerId, handleMouseEnter);
            map.off("mouseleave", layerId, handleMouseLeave);
        };
    }, [
        isLoaded,
        map,
        layerId,
        onClick,
        onMouseEnter,
        onMouseLeave,
        interactive,
    ]);
    return null;
}
function MapClusterLayer({ data, clusterMaxZoom = 14, clusterRadius = 50, clusterColors = ["#22c55e", "#eab308", "#ef4444"], clusterThresholds = [100, 750], pointColor = "#3b82f6", onPointClick, onClusterClick, }) {
    const { map, isLoaded } = useMap();
    const id = useId();
    const sourceId = `cluster-source-${id}`;
    const clusterLayerId = `clusters-${id}`;
    const clusterCountLayerId = `cluster-count-${id}`;
    const unclusteredLayerId = `unclustered-point-${id}`;
    const stylePropsRef = useRef({
        clusterColors,
        clusterThresholds,
        pointColor,
    });
    // Add source and layers on mount
    useEffect(() => {
        if (!isLoaded || !map)
            return;
        // Add clustered GeoJSON source
        map.addSource(sourceId, {
            type: "geojson",
            data,
            cluster: true,
            clusterMaxZoom,
            clusterRadius,
        });
        // Add cluster circles layer
        map.addLayer({
            id: clusterLayerId,
            type: "circle",
            source: sourceId,
            filter: ["has", "point_count"],
            paint: {
                "circle-color": [
                    "step",
                    ["get", "point_count"],
                    clusterColors[0],
                    clusterThresholds[0],
                    clusterColors[1],
                    clusterThresholds[1],
                    clusterColors[2],
                ],
                "circle-radius": [
                    "step",
                    ["get", "point_count"],
                    20,
                    clusterThresholds[0],
                    30,
                    clusterThresholds[1],
                    40,
                ],
                "circle-stroke-width": 1,
                "circle-stroke-color": "#fff",
                "circle-opacity": 0.85,
            },
        });
        // Add cluster count text layer
        map.addLayer({
            id: clusterCountLayerId,
            type: "symbol",
            source: sourceId,
            filter: ["has", "point_count"],
            layout: {
                "text-field": "{point_count_abbreviated}",
                "text-font": ["Open Sans"],
                "text-size": 12,
            },
            paint: {
                "text-color": "#fff",
            },
        });
        // Add unclustered point layer
        map.addLayer({
            id: unclusteredLayerId,
            type: "circle",
            source: sourceId,
            filter: ["!", ["has", "point_count"]],
            paint: {
                "circle-color": pointColor,
                "circle-radius": 5,
                "circle-stroke-width": 2,
                "circle-stroke-color": "#fff",
            },
        });
        return () => {
            try {
                if (map.getLayer(clusterCountLayerId))
                    map.removeLayer(clusterCountLayerId);
                if (map.getLayer(unclusteredLayerId))
                    map.removeLayer(unclusteredLayerId);
                if (map.getLayer(clusterLayerId))
                    map.removeLayer(clusterLayerId);
                if (map.getSource(sourceId))
                    map.removeSource(sourceId);
            }
            catch {
                // ignore
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, map, sourceId]);
    // Update source data when data prop changes (only for non-URL data)
    useEffect(() => {
        if (!isLoaded || !map || typeof data === "string")
            return;
        const source = map.getSource(sourceId);
        if (source) {
            source.setData(data);
        }
    }, [isLoaded, map, data, sourceId]);
    // Update layer styles when props change
    useEffect(() => {
        if (!isLoaded || !map)
            return;
        const prev = stylePropsRef.current;
        const colorsChanged = prev.clusterColors !== clusterColors ||
            prev.clusterThresholds !== clusterThresholds;
        // Update cluster layer colors and sizes
        if (map.getLayer(clusterLayerId) && colorsChanged) {
            map.setPaintProperty(clusterLayerId, "circle-color", [
                "step",
                ["get", "point_count"],
                clusterColors[0],
                clusterThresholds[0],
                clusterColors[1],
                clusterThresholds[1],
                clusterColors[2],
            ]);
            map.setPaintProperty(clusterLayerId, "circle-radius", [
                "step",
                ["get", "point_count"],
                20,
                clusterThresholds[0],
                30,
                clusterThresholds[1],
                40,
            ]);
        }
        // Update unclustered point layer color
        if (map.getLayer(unclusteredLayerId) && prev.pointColor !== pointColor) {
            map.setPaintProperty(unclusteredLayerId, "circle-color", pointColor);
        }
        stylePropsRef.current = { clusterColors, clusterThresholds, pointColor };
    }, [
        isLoaded,
        map,
        clusterLayerId,
        unclusteredLayerId,
        clusterColors,
        clusterThresholds,
        pointColor,
    ]);
    // Handle click events
    useEffect(() => {
        if (!isLoaded || !map)
            return;
        // Cluster click handler - zoom into cluster
        const handleClusterClick = async (e) => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: [clusterLayerId],
            });
            if (!features.length)
                return;
            const feature = features[0];
            const clusterId = feature.properties?.cluster_id;
            const pointCount = feature.properties?.point_count;
            const coordinates = feature.geometry.coordinates;
            if (onClusterClick) {
                onClusterClick(clusterId, coordinates, pointCount);
            }
            else {
                // Default behavior: zoom to cluster expansion zoom
                const source = map.getSource(sourceId);
                const zoom = await source.getClusterExpansionZoom(clusterId);
                map.easeTo({
                    center: coordinates,
                    zoom,
                });
            }
        };
        // Unclustered point click handler
        const handlePointClick = (e) => {
            if (!onPointClick || !e.features?.length)
                return;
            const feature = e.features[0];
            const coordinates = feature.geometry.coordinates.slice();
            // Handle world copies
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }
            onPointClick(feature, coordinates);
        };
        // Cursor style handlers
        const handleMouseEnterCluster = () => {
            map.getCanvas().style.cursor = "pointer";
        };
        const handleMouseLeaveCluster = () => {
            map.getCanvas().style.cursor = "";
        };
        const handleMouseEnterPoint = () => {
            if (onPointClick) {
                map.getCanvas().style.cursor = "pointer";
            }
        };
        const handleMouseLeavePoint = () => {
            map.getCanvas().style.cursor = "";
        };
        map.on("click", clusterLayerId, handleClusterClick);
        map.on("click", unclusteredLayerId, handlePointClick);
        map.on("mouseenter", clusterLayerId, handleMouseEnterCluster);
        map.on("mouseleave", clusterLayerId, handleMouseLeaveCluster);
        map.on("mouseenter", unclusteredLayerId, handleMouseEnterPoint);
        map.on("mouseleave", unclusteredLayerId, handleMouseLeavePoint);
        return () => {
            map.off("click", clusterLayerId, handleClusterClick);
            map.off("click", unclusteredLayerId, handlePointClick);
            map.off("mouseenter", clusterLayerId, handleMouseEnterCluster);
            map.off("mouseleave", clusterLayerId, handleMouseLeaveCluster);
            map.off("mouseenter", unclusteredLayerId, handleMouseEnterPoint);
            map.off("mouseleave", unclusteredLayerId, handleMouseLeavePoint);
        };
    }, [
        isLoaded,
        map,
        clusterLayerId,
        unclusteredLayerId,
        sourceId,
        onClusterClick,
        onPointClick,
    ]);
    return null;
}
export { Map, MapClusterLayer, MapControls, MapMarker, MapPopup, MapRoute, MarkerContent, MarkerLabel, MarkerPopup, MarkerTooltip, useMap, };
